// WHAT A RENDERED CARD MEASURES ABOUT ITSELF.
//
// This module runs INSIDE the page, after a real browser has laid the card
// out. That is the whole point: none of these numbers exist before layout, and
// every defect they catch shipped at least once while the JSON looked correct.
//
//   slackPx            twelve of fifteen 2026 templates were drafted too short
//                      for the 720px box — 84 to 241px of dead space pooled at
//                      the bottom, which reads as a card that has slipped up.
//   lineCount          an eyebrow that wraps to two lines. Invisible in JSON,
//                      invisible in a hex-and-font-stack review.
//   backgroundApplied  a background stored as `url("data:…")` gets wrapped by
//                      the renderer into `url(url("data:…"))`, which is invalid
//                      CSS, so the card silently falls back to flat colour.
//                      Several shipped templates have NEVER rendered their
//                      background. Nothing catches this today.
//   backgroundLoaded   a SECOND, unrelated way to lose a photograph: the CSS is
//                      perfectly valid, the browser accepts it, and the file
//                      404s. Measured 2026-08-19 against a deliberate control
//                      pointing at /assets/backgrounds/does-not-exist.jpg —
//                      `applied` true, the card white, and 17.22 contrast, which
//                      passes every check that existed. `applied` proves the
//                      DECLARATION was accepted; only this proves a photograph
//                      arrived. It matters more as the library grows: a few
//                      hundred harvested grounds plus customer uploads makes a
//                      moved or missing file routine rather than a typo.
//   fontAvailable      `document.fonts.check` asks the browser what it actually
//                      has, rather than comparing a declaration against the
//                      stylesheet href. A family index.html never loaded falls
//                      back to a generic serif and reads as a design choice.
//
// Contrast is NOT computed here. It cannot be: the ground under a line of text
// may be a photograph or a tiled pattern, and the DOM does not know what colour
// arrived at those pixels. The driver samples it from a text-hidden screenshot
// instead — see scripts/render.mjs.

/** The design-size card. Both numbers are fixed in InvitationPreviewV2. */
export const CARD_WIDTH = 528;
export const CARD_HEIGHT = 720;

export interface RowMetrics {
  id: string;
  /** 'text' | 'divider' | 'image' — as the DOM rendered it, not as declared. */
  kind: string;
  text: string;
  /** Line boxes the text actually occupied. */
  lineCount: number;
  /** Lines the CONTENT asks for — its explicit newlines. A row is wrapped when
   *  lineCount exceeds this, which is the only way to tell an unintended wrap
   *  from a deliberately two-line address. */
  declaredLineCount: number;
  heightPx: number;
  widthPx: number;
  /** The row's BLOCK box, relative to the card's top-left.
   *
   *  NOT where the ink is, and not what to sample for contrast: centred type
   *  occupies a fraction of a full-width block, so sampling this averages
   *  ground the letterforms never touch and reports a comfortable number for a
   *  label that has vanished. Use `lineRects()` for anything pixel-level. */
  box: { x: number; y: number; width: number; height: number };
  fontFamily: string;
  fontSizePx: number;
  color: string;
  /** Filled in by scripts/render.mjs, never by measureCard — see RowContrast. */
  contrast?: RowContrast;
}

/** WCAG contrast of a row's ink against the ground that actually arrived under
 *  it, in ratios (1 = invisible, 21 = black on white).
 *
 *  NOT computed here and never present in a `measureCard` result — the driver
 *  (`scripts/render.mjs`) fills it in from a second, text-hidden screenshot,
 *  because the ground may be a photograph or a tiled pattern and the DOM cannot
 *  know what colour arrived at those pixels. It is declared here anyway so the
 *  metric contract lives in ONE file: two components produce this record and a
 *  restatement in the driver is how the halves would drift.
 *
 *  Read MIN, not median. A footnote that half-vanishes into a damask has a
 *  perfectly respectable median and a min near 1. */
export interface RowContrast {
  min: number;
  /** 10th percentile — min alone can be a single unlucky pixel. */
  p10: number;
  median: number;
  max: number;
  samples: number;
}

export interface FontMetrics {
  family: string;
  /** False means the browser does not have it and substituted something. */
  available: boolean;
}

/**
 * The ground the card actually got — declared, accepted, and ARRIVED, which are
 * three different questions and have three different answers.
 *
 *   declaredType  what the design asked for.
 *   applied       whether the browser ACCEPTED the declaration. False catches
 *                 CSS the renderer built wrong (see `url(url("data:…"))` above).
 *   imagesLoaded  whether the bytes actually arrived and decoded. False catches
 *                 a valid declaration pointing at a file that is not there.
 *
 * A card can be `applied: true, imagesLoaded: false` and render as flat white
 * with a comfortable contrast score. That is the whole reason the second field
 * exists; do not collapse it into the first.
 */
export interface BackgroundMetrics {
  declaredType: string | null;
  /** A background was declared AND the browser accepted it. */
  applied: boolean;
  /** Every image URL the COMPUTED background-image really references, absolute
   *  as the browser resolved it. Empty for a solid colour and for a pure CSS
   *  gradient — neither of those fetches anything, and neither is a failure. */
  imageSources: string[];
  /** Did every one of `imageSources` fetch and decode to a non-zero bitmap?
   *
   *  NULL means the question does not apply: there are no image sources, so
   *  this is a solid ground, a CSS gradient, or a background the browser did
   *  not accept at all (which `applied` already reports). Null is NOT a
   *  failure, and a reader that treats it as one will condemn every gradient
   *  in the library.
   *
   *  Asked with `HTMLImageElement.decode()` rather than a fetch probe or a
   *  PerformanceResourceTiming lookup: decode() goes through the same image
   *  pipeline that painted the card and the same cache entry, answers
   *  identically for http(s), `data:` and `blob:` sources, and rejects on a
   *  zero-byte or corrupt file as well as a 404 — where a fetch probe happily
   *  200s on an HTML error page and a resource-timing entry exists for a 404
   *  exactly as it does for a hit. */
  imagesLoaded: boolean | null;
  computedImage: string;
  computedColor: string;
}

export interface CardMetrics {
  id: string;
  name: string;
  /** available − content. Target 25–80. Negative is overflow. */
  slackPx: number;
  overflowPx: number;
  contentHeightPx: number;
  availableHeightPx: number;
  /** The laid-out container. Above CARD_HEIGHT means content forced it taller. */
  boxHeightPx: number;
  padding: { top: number; right: number; bottom: number; left: number };
  background: BackgroundMetrics;
  rows: RowMetrics[];
  fonts: FontMetrics[];
  /** Families the design asked for that the browser does not have. */
  fontsFellBack: string[];
  wrappedRows: string[];
}

export interface LineBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * The line boxes a row's text actually occupies, in VIEWPORT coordinates —
 * where the ink is, as opposed to the block the row reserves.
 *
 * TEXT NODES ONLY. A range over an element's *contents* also yields a rect for
 * the element's own block box: full width, full height, wrapping all the
 * others. Counting that as a line added exactly one to EVERY row, so every card
 * in the library reported every row wrapped — and a guard that fires on
 * everything says nothing. (Measured 2026-08-19: eucalypt's one-line eyebrow
 * returned rects at top 122 h 18.5 — the block — and top 125 h 12 — the line.)
 *
 * Exported because the driver samples contrast INSIDE these rectangles: a
 * centred 10px label over a photograph occupies a fifth of the row's width, and
 * averaging the ground across the whole width measures ground the type never
 * touches. One implementation, two consumers.
 */
export function lineRects(el: Element): LineBox[] {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const rects: DOMRect[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.nodeValue?.trim()) continue;
    const range = document.createRange();
    range.selectNodeContents(node);
    for (const r of range.getClientRects()) if (r.width > 0 && r.height > 0) rects.push(r);
  }
  // Rects still split at inline boundaries, so merge any that share a line. By
  // vertical OVERLAP rather than a rounded top: an inline <em> at a different
  // size sits on the same baseline with a different top edge.
  const lines: LineBox[] = [];
  for (const r of rects) {
    const line = lines.find((l) => {
      const overlap = Math.min(l.bottom, r.bottom) - Math.max(l.top, r.top);
      return overlap > Math.min(l.bottom - l.top, r.height) * 0.5;
    });
    if (line) {
      line.left = Math.min(line.left, r.left);
      line.right = Math.max(line.right, r.right);
      line.top = Math.min(line.top, r.top);
      line.bottom = Math.max(line.bottom, r.bottom);
    } else {
      lines.push({ left: r.left, top: r.top, right: r.right, bottom: r.bottom });
    }
  }
  return lines.sort((a, b) => a.top - b.top);
}

/** First family of a computed font stack, unquoted. */
function firstFamily(stack: string): string {
  return (stack.split(',')[0] ?? '').replace(/['"]/g, '').trim();
}

const GENERIC_FAMILIES = new Set([
  'serif', 'sans-serif', 'cursive', 'monospace', 'fantasy', 'system-ui',
  // The ui-* keywords arrive from Tailwind's default stack, not from any
  // template, and are a request for whatever the platform has.
  'ui-serif', 'ui-sans-serif', 'ui-monospace', 'ui-rounded', 'math', 'emoji',
]);

/** A string whose width differs sharply between any two real typefaces. */
const WIDTH_PROBE = 'mmmmmmmmmmlliWWWWWWWW0Ogj';
const WIDTH_FALLBACKS = ['monospace', 'serif', 'sans-serif'];
let widthCanvas: HTMLCanvasElement | null = null;

/**
 * Did the browser really use this family, or substitute something?
 *
 * `document.fonts.check` ALONE CANNOT ANSWER THIS, and believing it did is the
 * defect this function exists to fix. FontFaceSet only knows about faces the
 * page DECLARED; for a family with no @font-face at all — `Pinyon Script`,
 * `Didot`, `Comic Sans MS`, the fourteen pre-2026 templates ask for families
 * index.html never loads — `check()` returns TRUE, because system fallback
 * counts as "available". Measured 2026-08-19: check('normal 400 34px "Bogus
 * Nonexistent Face"') === true. So the original check reported available for
 * every genuinely-missing font and unavailable for a Lato that was rendering
 * perfectly: it was incapable of ever being right in either direction.
 *
 * Two questions instead, in order:
 *   1. Is there a declared webfont face for this family that the browser has
 *      actually FETCHED? Then it is there, at whatever weight loaded.
 *   2. Otherwise the family could only come from the system — so MEASURE. Text
 *      set in `"<family>", monospace` is wider or narrower than the same text
 *      in bare `monospace` if and only if the family resolved to something
 *      else. Three fallbacks, because a family could coincidentally match the
 *      metrics of one of them.
 */
function familyAvailable(family: string, spec: string): boolean {
  if (GENERIC_FAMILIES.has(family.toLowerCase())) return true;
  const want = family.toLowerCase();
  for (const face of document.fonts) {
    if (face.family.replace(/['"]/g, '').toLowerCase() === want && face.status === 'loaded') {
      return true;
    }
  }
  widthCanvas ??= document.createElement('canvas');
  const ctx = widthCanvas.getContext('2d');
  if (!ctx) return document.fonts.check(`${spec} "${family}"`);
  const width = (stack: string) => {
    ctx.font = `${spec} ${stack}`;
    return ctx.measureText(WIDTH_PROBE).width;
  };
  return WIDTH_FALLBACKS.some((fb) => width(`"${family}", ${fb}`) !== width(fb));
}

/**
 * The image URLs a computed `background-image` really references.
 *
 * Parsed from the COMPUTED value, not the declared one, so it sees exactly what
 * the browser is going to fetch and nothing it isn't: an invalid declaration
 * computes to `none` and yields nothing here, and a gradient yields nothing
 * because a gradient fetches nothing. Chrome absolutises relative URLs on the
 * way through, so these are resolvable as-is.
 */
export function backgroundImageSources(computedImage: string): string[] {
  if (!computedImage || computedImage === 'none') return [];
  const out: string[] = [];
  for (const m of computedImage.matchAll(/url\((?:"((?:[^"\\]|\\.)*)"|'((?:[^'\\]|\\.)*)'|([^)]*))\)/g)) {
    const raw = m[1] ?? m[2] ?? m[3] ?? '';
    const url = raw.trim();
    if (url) out.push(url);
  }
  return out;
}

/**
 * Did this source fetch and decode to an actual bitmap? See imagesLoaded.
 *
 * Retried ONCE for network-backed sources. Measured 2026-08-19: a run that
 * reused a dev server which happened to be shutting down reported the real,
 * committed serpentine.jpg as NOT LOADED — a refused connection is
 * indistinguishable from a 404 at this layer, and a false "the photograph is
 * missing" is exactly the reading this metric exists to make trustworthy. A
 * retry cannot rescue a genuine 404 (it 404s again), so it costs one wasted
 * request per real defect and buys out the transient case.
 */
async function imageDecodes(src: string): Promise<boolean> {
  const attempts = src.startsWith('data:') ? 1 : 2;
  for (let i = 0; i < attempts; i++) {
    const img = new Image();
    img.src = src;
    try {
      await img.decode();
    } catch {
      continue;
    }
    // decode() can resolve for a source with no intrinsic size; a ground that
    // paints nothing is not a ground that loaded.
    return img.naturalWidth > 0 && img.naturalHeight > 0;
  }
  return false;
}

/**
 * Measure one rendered card.
 *
 * @param root the `.invitation-preview-content` element
 * @param design the design it was rendered from, for the DECLARED half of the
 *   comparison — every measurement that found something compared two things.
 */
export async function measureCard(root: HTMLElement, design: any): Promise<CardMetrics> {
  const cs = getComputedStyle(root);
  const rootBox = root.getBoundingClientRect();
  const pad = {
    top: parseFloat(cs.paddingTop) || 0,
    right: parseFloat(cs.paddingRight) || 0,
    bottom: parseFloat(cs.paddingBottom) || 0,
    left: parseFloat(cs.paddingLeft) || 0,
  };

  const wrapper = root.querySelector('.invitation-preview-rows') as HTMLElement | null;
  const contentHeightPx = wrapper ? wrapper.getBoundingClientRect().height : 0;

  // Against the DESIGN height, not the laid-out height: a card whose content
  // overflowed grew its own container, and measuring slack against the grown
  // container would report a comfortable 0 for the one case that is broken.
  const availableHeightPx = CARD_HEIGHT - pad.top - pad.bottom;
  const slackPx = Math.round(availableHeightPx - contentHeightPx);

  // The DECLARED half of every row comparison, keyed by the id the DOM carries.
  const declared = new Map<string, any>();
  for (const row of design?.content ?? []) if (row?.id) declared.set(row.id, row);

  const rows: RowMetrics[] = [];
  for (const el of [...(wrapper?.children ?? [])] as HTMLElement[]) {
    const box = el.getBoundingClientRect();
    if (box.height === 0 && box.width === 0) continue; // spacer rows render null
    const rcs = getComputedStyle(el);
    const text = (el.textContent ?? '').trim();
    const id = el.dataset.rowId ?? '';
    const content: string = declared.get(id)?.content ?? '';
    rows.push({
      id,
      kind: el.dataset.rowType ?? (text ? 'text' : 'other'),
      text,
      lineCount: text ? lineRects(el).length : 0,
      declaredLineCount: content ? content.split('\n').length : text ? 1 : 0,
      heightPx: Math.round(box.height * 100) / 100,
      widthPx: Math.round(box.width * 100) / 100,
      box: {
        x: Math.round(box.left - rootBox.left),
        y: Math.round(box.top - rootBox.top),
        width: Math.round(box.width),
        height: Math.round(box.height),
      },
      fontFamily: firstFamily(rcs.fontFamily),
      fontSizePx: parseFloat(rcs.fontSize) || 0,
      color: rcs.color,
    });
  }

  // Ask the browser what it HAS, rather than comparing declarations against
  // the stylesheet href — a font can be declared, listed, and still absent.
  //
  // AT THE WEIGHT AND STYLE THE ROW ACTUALLY USES, which is the whole
  // difference between a guard and a nuisance. `document.fonts.check` answers
  // "is a face MATCHING this shorthand loaded", and a webfont face loads only
  // when something on the page uses it. index.html asks Google Fonts for
  // `Lato:wght@400;700`; eucalypt's labels are Lato 700, so the 700 face loads
  // and the 400 face stays `unloaded` — and the old `16px "Lato"` (weight 400
  // by default) therefore reported LATO MISSING on a card that was rendering
  // Lato correctly. Measured 2026-08-19: check('16px "Lato"') false,
  // check('700 10px "Lato"') true, with the 700 FontFace status 'loaded'.
  const specs = new Map<string, string>();
  for (const el of [...(wrapper?.children ?? [])] as HTMLElement[]) {
    const rcs = getComputedStyle(el);
    const family = firstFamily(rcs.fontFamily);
    if (!family || specs.has(family)) continue;
    specs.set(family, `${rcs.fontStyle} ${rcs.fontWeight} ${parseFloat(rcs.fontSize) || 16}px`);
  }
  const families = new Set<string>();
  for (const r of rows) if (r.fontFamily) families.add(r.fontFamily);
  const theme = design?.template?.theme ?? {};
  for (const f of [theme.fontFamily, theme.secondaryFontFamily]) {
    if (typeof f === 'string' && f) families.add(firstFamily(f));
  }
  const fonts: FontMetrics[] = [...families].map((family) => ({
    family,
    available: familyAvailable(family, specs.get(family) ?? 'normal 400 16px'),
  }));

  const declaredType: string | null = design?.template?.background?.type ?? null;
  const computedImage = cs.backgroundImage;
  const hasImage = computedImage !== 'none' && computedImage !== '';
  const applied =
    declaredType === 'solid' || declaredType === null
      ? true // a solid ground is a colour; there is nothing to fail to load
      : hasImage;

  // ACCEPTED is not ARRIVED. Every source is probed, and one failure fails the
  // card: a layered ground that lost one of its two images is broken, and
  // reporting the majority verdict would hide it.
  const imageSources = backgroundImageSources(computedImage);
  const decoded = await Promise.all(imageSources.map(imageDecodes));
  const imagesLoaded = imageSources.length === 0 ? null : decoded.every(Boolean);

  return {
    id: design?.template?.id ?? '',
    name: design?.template?.name ?? '',
    slackPx,
    overflowPx: Math.max(0, -slackPx),
    contentHeightPx: Math.round(contentHeightPx * 100) / 100,
    availableHeightPx,
    boxHeightPx: Math.round(rootBox.height * 100) / 100,
    padding: pad,
    background: {
      declaredType,
      applied,
      imageSources,
      imagesLoaded,
      computedImage: computedImage.slice(0, 120),
      computedColor: cs.backgroundColor,
    },
    rows,
    fonts,
    fontsFellBack: fonts.filter((f) => !f.available).map((f) => f.family).sort(),
    wrappedRows: rows.filter((r) => r.lineCount > r.declaredLineCount).map((r) => r.id),
  };
}
